import { Button, ButtonStyles, Icon, Icons, Popup } from '@silverfort/sf-components';
import React, { useCallback } from 'react';
import { isNewerVersion } from '@silverfort-nodelibs/common/utils/common';
import moment from 'moment';
import { DcComponent, NodeComponent, PushServiceComponent, SoftwareStatus } from './types';

const EnvironmentStatus: React.FC<{
    softwareStatus: SoftwareStatus;
    disableUpgrade?: boolean;
    isEditor?: boolean;
    serversData?: {
        nodes: NodeComponent[];
        dcs: DcComponent[];
        pushServices: PushServiceComponent[];
    };
    onUpgradeClick: () => void;
    onContinueClick: () => void;
}> = ({ disableUpgrade, softwareStatus, isEditor, onUpgradeClick, onContinueClick, serversData }) => {
    const currentVersion = softwareStatus.currentTowerVersion || '';
    let upgradeableVersion = '';

    const [showNewUpgradeModal, setShowNewUpgradeModal] = React.useState(false);

    const hasOlderComponent = useCallback(() => {
        const towerVersion = softwareStatus.currentTowerVersion;
        const componentsToCheck = {
            nodes: serversData?.nodes,
            pushServices: serversData?.pushServices
        };

        const dcsToCheck = softwareStatus.autoADAdapterUpgrade ? [] : serversData?.dcs;

        const componentVersions = new Set(
            Object.values(componentsToCheck)
                .flat()
                .map((component) => component?.version)
        );
        const hasOlderComponent = [...componentVersions].find((version) => isNewerVersion(towerVersion, version));

        const dcsVersions = new Set(dcsToCheck?.map((dc) => dc?.version).filter((version) => version !== undefined));
        const hasOlderDc = [...dcsVersions].find((version) =>
            isNewerVersion(softwareStatus.currentCastleVersion, version)
        );

        const hasLatestImage = softwareStatus.lastDownloadedVersion === softwareStatus.currentTowerVersion;
        return (hasOlderComponent || hasOlderDc) && hasLatestImage;
    }, [
        serversData,
        softwareStatus.currentTowerVersion,
        softwareStatus.currentCastleVersion,
        softwareStatus.lastDownloadedVersion,
        softwareStatus.autoADAdapterUpgrade
    ]);

    const isWizardActionInProgress =
        softwareStatus.upgradingNodes ||
        softwareStatus.upgradingPushServices ||
        softwareStatus.upgradingTower ||
        softwareStatus.downloading ||
        (softwareStatus.upgradingDcs && !softwareStatus.autoADAdapterUpgrade);

    const calcVersionStatus = () => {
        let statusIcon = <Icon size={24} icon={Icons.CircleCheck} className="text-sf-positive-500" />;
        let downloadVersionDesc = 'Your environment is up to date';
        if (softwareStatus.versionUpgradeInProgress || hasOlderComponent() || isWizardActionInProgress) {
            upgradeableVersion = softwareStatus.versionUpgradeInProgress || currentVersion;
            downloadVersionDesc = `Proceed with the upgrade of version ${upgradeableVersion}`;
            statusIcon = <Icon size={24} icon={Icons.Sync} className="text-sf-primary-500 rotate-90 scale-x-[-1]" />;
        }

        if (
            softwareStatus.maxTowerVersion &&
            isNewerVersion(softwareStatus.maxTowerVersion, currentVersion) &&
            isNewerVersion(softwareStatus.maxTowerVersion, softwareStatus.lastDownloadedVersion)
        ) {
            upgradeableVersion = softwareStatus.maxTowerVersion;
            downloadVersionDesc = `New version ${upgradeableVersion} is available`;
            statusIcon = <Icon size={24} icon={Icons.Download} className="text-sf-primary-500" />;
        }

        if (softwareStatus.downloadError !== '' || softwareStatus.towerUpgradeError !== '') {
            downloadVersionDesc = 'An error occurred during the upgrade process';
            statusIcon = <Icon size={24} icon={Icons.Warning} className="text-sf-neutral-500" />;
        }

        return (
            <div className="flex gap-2 items-center text-16 text-sf-gray-700">
                {statusIcon} {downloadVersionDesc}
            </div>
        );
    };

    const showAdditionalContinueButton: boolean =
        !!softwareStatus.versionUpgradeInProgress &&
        !!softwareStatus.maxTowerVersion &&
        isNewerVersion(softwareStatus.maxTowerVersion, softwareStatus.versionUpgradeInProgress);

    const handleUpgradeClick = () => {
        if (showAdditionalContinueButton) {
            setShowNewUpgradeModal(true);
        } else {
            onUpgradeClick();
        }
    };

    return (
        <>
            <div className="p-6 h-[164px]">
                <div className="p-6 w-full justify-between flex border border-sf-gray-100 rounded-lg">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center p-3 gap-3">
                            <img className={'max-h-8 max-w-8'} src={softwareStatus.companyLogo} alt="" />
                            <span className="font-sf-poppins text-18 font-medium text-sf-gray-800">
                                {softwareStatus.companyName}
                            </span>
                        </div>
                        <div className="flex items-center justify-center">
                            <span className="w-px h-5 bg-sf-gray-300"></span>
                        </div>
                        <div className="flex flex-col gap-3 px-3 py-1">
                            {calcVersionStatus()}
                            <div className="flex gap-3">
                                <div className="bg-sf-primary-100 flex flex-wrap items-center text-sf-primary-500 min-h-6 px-2 rounded text-12 font-medium text-[#0D71C5]">
                                    Current Admin Console version: <span className="pl-1">{currentVersion}</span>
                                </div>
                                <div className="text-sf-gray-400 h-6 text-13 flex items-center">
                                    Last check:
                                    <span className="pl-1">
                                        {moment(softwareStatus.lastCheck).format('HH:mm:ss DD/MM/yyyy')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {upgradeableVersion && (
                        <div className="flex justify-end items-end gap-2">
                            {/* In case a newer version is available than the current upgrade process version, suggest to */}
                            {/* continue the current process anyway */}
                            {showAdditionalContinueButton && (
                                <Button
                                    disabled={disableUpgrade || !isEditor}
                                    onClick={onContinueClick}
                                    buttonStyle={ButtonStyles.TertiaryBorder}
                                    text={`Continue ${softwareStatus.versionUpgradeInProgress} upgrade`}></Button>
                            )}
                            {/* If current upgrade process version is the latest version, show continue button */}
                            {!showAdditionalContinueButton &&
                            (isWizardActionInProgress ||
                                (softwareStatus.versionUpgradeInProgress &&
                                    softwareStatus.versionUpgradeInProgress === softwareStatus.maxTowerVersion)) ? (
                                <Button
                                    disabled={disableUpgrade || !isEditor}
                                    onClick={onContinueClick}
                                    buttonStyle={ButtonStyles.PrimarySolid}
                                    text={`Continue ${upgradeableVersion} upgrade`}></Button>
                            ) : (
                                <Button
                                    disabled={disableUpgrade || !isEditor}
                                    onClick={handleUpgradeClick}
                                    buttonStyle={ButtonStyles.PrimarySolid}
                                    text={`Upgrade to ${upgradeableVersion}`}></Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {showNewUpgradeModal && (
                <Popup
                    FooterComponent={() => (
                        <div className="flex items-center justify-end gap-2">
                            <Button
                                buttonStyle={ButtonStyles.TertiaryBorder}
                                text={`Close`}
                                onClick={() => {
                                    setShowNewUpgradeModal(false);
                                }}
                            />
                            <Button
                                buttonStyle={ButtonStyles.PrimarySolid}
                                text={`Upgrade to ${upgradeableVersion}`}
                                onClick={() => {
                                    onUpgradeClick();
                                    setShowNewUpgradeModal(false);
                                }}
                            />
                        </div>
                    )}
                    open={!!showNewUpgradeModal}
                    title={`Upgrade to ${upgradeableVersion}`}
                    ContentComponent={() => (
                        <div>
                            {`Starting a new upgrade process to version ${upgradeableVersion} will replace the ongoing upgrade process on version ${softwareStatus.versionUpgradeInProgress}. If you proceed, you won't be able to continue with the upgrade to ${softwareStatus.versionUpgradeInProgress}. Do you want to continue?`}
                        </div>
                    )}
                    onClose={() => {
                        setShowNewUpgradeModal(false);
                    }}
                />
            )}
        </>
    );
};

export default EnvironmentStatus;
